/**
 * Interface Language (feature guide)
 *
 * Screenshots were taken from the Sokuji v0.41.1 desktop app.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "Interface Language",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "Sokuji’s menus and buttons are available in 30 languages. The interface language is separate from the languages you translate between."
  },
  "steps": [
    {
      "title": "In the setup guide",
      "content": "The first step of the setup guide asks which language Sokuji should use. Pick yours from <strong>Interface language</strong>.",
      "screenshot": "01-setup-language.png"
    },
    {
      "title": "Change it later",
      "content": "In Settings, under <strong>Help</strong>, choose a language from the menu with the globe. It cannot change while a session runs.",
      "screenshot": "02-help-language.png"
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "The languages you translate between are set under Translation Languages, not here."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "Some text is still in English.",
        "answer": "Where a translation is missing, Sokuji shows the English text."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "界面语言",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "Sokuji 的菜单和按钮提供 30 种语言。界面语言与你用来互译的语言相互独立。"
  },
  "steps": [
    {
      "title": "在设置向导中",
      "content": "设置向导的第一步会询问 Sokuji 应该使用哪种语言。在<strong>界面语言</strong>中选择你的语言。",
      "screenshot": "01-setup-language.png"
    },
    {
      "title": "稍后更改",
      "content": "在设置的<strong>帮助</strong>下，从带有地球图标的菜单中选择一种语言。会话运行期间无法更改。",
      "screenshot": "02-help-language.png"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "你用来互译的语言在“翻译语言”下设置，而不是在这里。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "有些文字仍然是英文。",
        "answer": "如果缺少某处的翻译，Sokuji 会显示英文原文。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "インターフェース言語",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "Sokuji のメニューとボタンは 30 の言語で利用できます。表示言語は、翻訳に使う言語とは別に設定します。"
  },
  "steps": [
    {
      "title": "セットアップガイドで",
      "content": "セットアップガイドの最初のステップで、Sokuji の表示言語を尋ねられます。<strong>表示言語</strong>から選んでください。",
      "screenshot": "01-setup-language.png"
    },
    {
      "title": "あとで変更する",
      "content": "設定の<strong>ヘルプ</strong>で、地球儀アイコンのメニューから言語を選びます。セッションの実行中は変更できません。",
      "screenshot": "02-help-language.png"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "翻訳に使う言語は、ここではなく「翻訳言語」で設定します。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "一部のテキストが英語のままです。",
        "answer": "翻訳がない箇所では、Sokuji は英語のテキストを表示します。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "인터페이스 언어",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "Sokuji의 메뉴와 버튼은 30개 언어로 제공됩니다. 인터페이스 언어는 번역에 사용하는 언어와는 별개입니다."
  },
  "steps": [
    {
      "title": "설정 가이드에서",
      "content": "설정 가이드의 첫 단계에서 Sokuji가 사용할 언어를 묻습니다. <strong>인터페이스 언어</strong>에서 원하는 언어를 고르세요.",
      "screenshot": "01-setup-language.png"
    },
    {
      "title": "나중에 변경",
      "content": "설정의 <strong>도움말</strong>에서 지구본 아이콘이 있는 메뉴로 언어를 고르세요. 세션이 실행 중일 때는 바꿀 수 없습니다.",
      "screenshot": "02-help-language.png"
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "번역에 사용하는 언어는 여기가 아니라 “번역 언어”에서 설정합니다."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "일부 텍스트가 아직 영어로 나옵니다.",
        "answer": "번역이 없는 부분에서는 Sokuji가 영어 텍스트를 표시합니다."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "Idioma de la interfaz",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "Los menús y botones de Sokuji están disponibles en 30 idiomas. El idioma de la interfaz es independiente de los idiomas entre los que traduces."
  },
  "steps": [
    {
      "title": "En la guía de configuración",
      "content": "El primer paso de la guía de configuración pregunta qué idioma debe usar Sokuji. Elige el tuyo en <strong>Idioma de la interfaz</strong>.",
      "screenshot": "01-setup-language.png"
    },
    {
      "title": "Cámbialo más tarde",
      "content": "En Configuración, en <strong>Ayuda</strong>, elige un idioma en el menú del globo terráqueo. No puede cambiar mientras hay una sesión en marcha.",
      "screenshot": "02-help-language.png"
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "Los idiomas entre los que traduces se configuran en Idiomas de Traducción, no aquí."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "Parte del texto sigue en inglés.",
        "answer": "Donde falta una traducción, Sokuji muestra el texto en inglés."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Langue de l'interface",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "Les menus et les boutons de Sokuji sont disponibles en 30 langues. La langue de l’interface est distincte des langues entre lesquelles vous traduisez."
  },
  "steps": [
    {
      "title": "Dans le guide de configuration",
      "content": "La première étape du guide de configuration demande quelle langue Sokuji doit utiliser. Choisissez la vôtre dans <strong>Langue de l'interface</strong>.",
      "screenshot": "01-setup-language.png"
    },
    {
      "title": "Changez-la plus tard",
      "content": "Dans les Paramètres, sous <strong>Aide</strong>, choisissez une langue dans le menu marqué d’un globe. Elle ne peut pas changer pendant une session.",
      "screenshot": "02-help-language.png"
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "Les langues entre lesquelles vous traduisez se règlent sous Langues de Traduction, pas ici."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "Une partie du texte est encore en anglais.",
        "answer": "Quand une traduction manque, Sokuji affiche le texte anglais."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "لغة الواجهة",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "تتوفر قوائم Sokuji وأزراره بـ 30 لغة. لغة الواجهة منفصلة عن اللغات التي تترجم بينها."
  },
  "steps": [
    {
      "title": "في دليل الإعداد",
      "content": "تسألك الخطوة الأولى من دليل الإعداد عن اللغة التي يجب أن يستخدمها Sokuji. اختر لغتك من <strong>لغة الواجهة</strong>.",
      "screenshot": "01-setup-language.png"
    },
    {
      "title": "غيّرها لاحقًا",
      "content": "في الإعدادات، تحت <strong>مساعدة</strong>، اختر لغة من القائمة التي تحمل رمز الكرة الأرضية. لا يمكن تغييرها أثناء تشغيل جلسة.",
      "screenshot": "02-help-language.png"
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "تُضبط اللغات التي تترجم بينها تحت «لغات الترجمة»، لا هنا."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "بعض النصوص لا تزال بالإنجليزية.",
        "answer": "حيثما تكون الترجمة مفقودة، يعرض Sokuji النص الإنجليزي."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "Idioma da interface",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "Os menus e botões do Sokuji estão disponíveis em 30 idiomas. O idioma da interface é independente dos idiomas entre os quais você traduz."
  },
  "steps": [
    {
      "title": "No guia de configuração",
      "content": "O primeiro passo do guia de configuração pergunta qual idioma o Sokuji deve usar. Escolha o seu em <strong>Idioma da interface</strong>.",
      "screenshot": "01-setup-language.png"
    },
    {
      "title": "Altere depois",
      "content": "Em Configurações, em <strong>Ajuda</strong>, escolha um idioma no menu com o globo. Ele não pode ser alterado durante uma sessão.",
      "screenshot": "02-help-language.png"
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "Os idiomas entre os quais você traduz são definidos em Idiomas de Tradução, não aqui."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "Parte do texto continua em inglês.",
        "answer": "Quando falta uma tradução, o Sokuji mostra o texto em inglês."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Язык интерфейса",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "Меню и кнопки Sokuji доступны на 30 языках. Язык интерфейса не зависит от языков, между которыми вы переводите."
  },
  "steps": [
    {
      "title": "В мастере настройки",
      "content": "На первом шаге мастера настройки нужно выбрать язык Sokuji. Выберите свой в поле <strong>Язык интерфейса</strong>.",
      "screenshot": "01-setup-language.png"
    },
    {
      "title": "Смените его позже",
      "content": "В настройках, в разделе <strong>Помощь</strong>, выберите язык в меню со значком глобуса. Во время сеанса его изменить нельзя.",
      "screenshot": "02-help-language.png"
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "Языки, между которыми вы переводите, задаются в разделе «Языки перевода», а не здесь."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Часть текста всё ещё на английском.",
        "answer": "Там, где перевода нет, Sokuji показывает английский текст."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Sprache der Benutzeroberfläche",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "Die Menüs und Schaltflächen von Sokuji gibt es in 30 Sprachen. Die Sprache der Benutzeroberfläche ist unabhängig von den Sprachen, zwischen denen Sie übersetzen."
  },
  "steps": [
    {
      "title": "In der Einrichtung",
      "content": "Der erste Schritt der Einrichtung fragt, welche Sprache Sokuji verwenden soll. Wählen Sie Ihre unter <strong>Sprache der Benutzeroberfläche</strong>.",
      "screenshot": "01-setup-language.png"
    },
    {
      "title": "Später ändern",
      "content": "Wählen Sie in den Einstellungen unter <strong>Hilfe</strong> eine Sprache im Menü mit dem Globus. Während einer Sitzung lässt sie sich nicht ändern.",
      "screenshot": "02-help-language.png"
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Die Sprachen, zwischen denen Sie übersetzen, stellen Sie unter „Übersetzungssprachen“ ein, nicht hier."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Manche Texte sind noch auf Englisch.",
        "answer": "Wo eine Übersetzung fehlt, zeigt Sokuji den englischen Text."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Мова інтерфейсу",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "Меню та кнопки Sokuji доступні 30 мовами. Мова інтерфейсу не залежить від мов, між якими ви перекладаєте."
  },
  "steps": [
    {
      "title": "У майстрі налаштування",
      "content": "На першому кроці майстра налаштування потрібно вибрати мову Sokuji. Виберіть свою в полі <strong>Мова інтерфейсу</strong>.",
      "screenshot": "01-setup-language.png"
    },
    {
      "title": "Змініть її пізніше",
      "content": "У налаштуваннях, у розділі <strong>Допомога</strong>, виберіть мову в меню зі значком глобуса. Під час сеансу її змінити не можна.",
      "screenshot": "02-help-language.png"
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "Мови, між якими ви перекладаєте, задаються в розділі «Мови перекладу», а не тут."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Частина тексту досі англійською.",
        "answer": "Там, де перекладу немає, Sokuji показує англійський текст."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Lingua dell'interfaccia",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "Menu e pulsanti di Sokuji sono disponibili in 30 lingue. La lingua dell’interfaccia è indipendente dalle lingue tra cui traduci."
  },
  "steps": [
    {
      "title": "Nella configurazione guidata",
      "content": "Il primo passaggio della configurazione guidata chiede quale lingua deve usare Sokuji. Scegli la tua in <strong>Lingua dell'Interfaccia</strong>.",
      "screenshot": "01-setup-language.png"
    },
    {
      "title": "Cambiala in seguito",
      "content": "In Impostazioni, sotto <strong>Aiuto</strong>, scegli una lingua dal menu con il globo. Non può cambiare mentre una sessione è attiva.",
      "screenshot": "02-help-language.png"
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "Le lingue tra cui traduci si impostano in Lingue di Traduzione, non qui."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "Parte del testo è ancora in inglese.",
        "answer": "Dove manca una traduzione, Sokuji mostra il testo in inglese."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function FeatureInterfaceLanguage() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/interface-language" />;
}
